import {
  useWSSnackbar,
  WSDateRangeSelectOptions
} from "@wingspanhq/fe-component-library";
import {
  IInvoice,
  IInvoiceLineItem,
  IInvoiceTemplate,
  ScheduleStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  RecurrenceScheduleDateStatus,
  RecurringInvoice,
  RecurringLineItem
} from "@wingspanhq/wingspan-api-sdk";
import { wsMoment as moment } from "@wingspanhq/utils/dist/date/wsMoment";
import min from "lodash/min";
import queryString from "qs";
import { useSession } from "@hooks/useSession";
import { queriesAccounts } from "@query";
import { selectorAccountDisplayName } from "../../shared/selectors/selectorAccountDisplayName";
import { calculateDayDifference } from "../../utils/dates";
import { downloadFile } from "../../utils/files";

export const getInvoiceTemplateDueInDays = (
  invoiceTemplate: IInvoiceTemplate
) => {
  const sendDate =
    min([
      invoiceTemplate.scheduleDates?.[0]?.date,
      invoiceTemplate.frequency?.startDate
    ]) || new Date();
  const dueDate = invoiceTemplate.invoiceData.dueDate;
  return calculateDayDifference(dueDate, sendDate);
};

export const calculateInvoiceTempalteDueDate = (
  invoiceTemplate: IInvoiceTemplate
) => {
  const dueInDays = getInvoiceTemplateDueInDays(invoiceTemplate);

  const nextInvoiceDate =
    invoiceTemplate.scheduleDates?.[0] &&
    invoiceTemplate.scheduleDates[0].status === ScheduleStatus.Pending
      ? invoiceTemplate.scheduleDates[0].date
      : invoiceTemplate.nextInvoiceDate;

  return moment(nextInvoiceDate).add(dueInDays, "days").toDate();
};

export const calculateRecurringInvoiceDueDate = (
  recurringInvoice: RecurringInvoice
) => {
  const nextOccurrenceDate =
    recurringInvoice.nextOccurrenceDate ??
    recurringInvoice.schedule?.scheduleDates?.find(
      scheduleDate =>
        scheduleDate.status === RecurrenceScheduleDateStatus.PENDING
    )?.date;

  return moment(nextOccurrenceDate ?? new Date())
    .add(recurringInvoice.invoiceTemplate?.paymentTermsDays ?? 0, "days")
    .toDate();
};

export const calculateLineItemRegularPrice = (lineItem: IInvoiceLineItem) =>
  lineItem.unit ? lineItem.costPerUnit * lineItem.quantity : lineItem.totalCost;

export const calculateLineItemDiscount = (lineItem: IInvoiceLineItem) => {
  if (lineItem.discount) {
    if (lineItem.discount.amount) {
      return lineItem.discount.amount;
    }

    if (lineItem.discount.percentage) {
      const regularPrice = calculateLineItemRegularPrice(lineItem);
      return (regularPrice * lineItem.discount.percentage) / 100;
    }
  }
};

export const calculateLineItemTotal = (
  lineItem: IInvoiceLineItem,
  options?: { applyDiscount?: boolean }
) => {
  const regularPrice = calculateLineItemRegularPrice(lineItem);

  if (options?.applyDiscount) {
    const discount = calculateLineItemDiscount(lineItem);
    if (discount) {
      return regularPrice - discount;
    }
  }
  return regularPrice;
};

export const calculateLineItemsTotal = (lineItems: IInvoiceLineItem[]) =>
  lineItems.reduce((total, lineItem) => {
    return total + calculateLineItemTotal(lineItem, { applyDiscount: true });
  }, 0);

export const calculateRecurringLineItemsTotal = (
  lineItems: RecurringLineItem[] = []
) =>
  lineItems.reduce((total, lineItem) => {
    const regularPrice = lineItem.unit
      ? (lineItem.unitCost ?? 0) * (lineItem.quantity ?? 0)
      : (lineItem.totalCost ?? 0);

    if (lineItem.discount?.amount) {
      return total + regularPrice - lineItem.discount.amount;
    }

    if (lineItem.discount?.percentage) {
      return (
        total +
        regularPrice -
        (regularPrice * lineItem.discount.percentage) / 100
      );
    }

    return total + regularPrice;
  }, 0);

export const timeToQuantity = (hours: number, minutes: number): number =>
  hours + Math.round((minutes / 60) * 100) / 100;

export const quantityToTime = (quantity: number): [number, number] => {
  const hours = Math.trunc(quantity);
  const minutes = Math.round(quantity * 60) % 60;
  return [hours, minutes];
};

export const calculateInvoicesTotal = (invoices: IInvoice[]) =>
  invoices.reduce(
    (total, invoice) => total + calculateLineItemsTotal(invoice.lineItems),
    0
  );

export const calculateInvoicesTotalMemberGross = (invoices: IInvoice[]) =>
  invoices.reduce(
    (total, invoice) =>
      total + Math.abs(invoice.amountDetails?.memberGross || 0),
    0
  );

export const INVOICE_ACCOUNT_NAME = "Invoice Payouts Account";

export const useDownloadPdf = () => {
  const { openSnackbar } = useWSSnackbar();

  return (url: string, filename?: string) => {
    downloadFile(url, filename);
    openSnackbar({
      message: "PDF downloaded"
    });
  };
};

export const useDefaultInvoiceSubject = () => {
  const session = useSession();
  const queryAccount = queriesAccounts.getAccount.useQuery({
    accountId: session.accountId
  });

  return `Payment requested - New invoice from ${
    queryAccount.data ? selectorAccountDisplayName(queryAccount.data) : "TDB"
  }`;
};

export enum DateRangeFilter {
  All = "All",
  PastWeek = "PastWeek",
  PastMonth = "PastMonth",
  PastYear = "PastYear",
  Custom = "Custom"
}

export const getDateRangeFilterRanges = (
  range: DateRangeFilter,
  customRange?: Date[]
): [Date, Date] | null => {
  switch (range) {
    case DateRangeFilter.Custom:
      if (customRange) {
        return [customRange[0], customRange[1]];
      } else {
        return null;
      }

    case DateRangeFilter.PastWeek:
      return WSDateRangeSelectOptions.PastWeek.range;

    case DateRangeFilter.PastMonth:
      return WSDateRangeSelectOptions.PastMonth.range;

    case DateRangeFilter.PastYear:
      return WSDateRangeSelectOptions.PastMonth.range;

    default:
      return null;
  }
};

export const getAutopayLink = (params: {
  payeeId?: string;
  companyName: string;
  tag: string;
  email: string;
}) => {
  const searchParams = {
    companyName: params.companyName,
    p: params.payeeId,
    email: params.email
  };
  // /autopay/<tag>?growth_name=belay&companyName=BELAY&mc=<memberClientId>&email=<email>&enabled_payment_methods=credit,ach,manual
  return `${window.location.host}/autopay/${params.tag}?${queryString.stringify(
    searchParams
  )}`;
};
